"use client"

import { CircleDashed } from "lucide-react"
import { useMprcSocket } from "@/hooks/use-mprc-socket"
import { Header } from "@/components/dashboard/Header"
import { PowerIndicator } from "@/components/dashboard/PowerIndicator"
import { RobotStatusBar } from "@/components/dashboard/RobotStatusBar"
import { StationCard } from "@/components/dashboard/StationCard"
import { CameraStream } from "@/components/dashboard/CameraStream"
import { StepModelViewer } from "@/components/dashboard/StepModelViewer"
import { Badge } from "@/components/ui/badge"
import { STATIONS, SYSTEM_NODES } from "@/lib/mprc-config"

export default function DashboardPage() {
  const { data, isConnected } = useMprcSocket()

  const isPowered = Boolean(data[SYSTEM_NODES.power])
  const isManual = Boolean(data[SYSTEM_NODES.manualMode])

  const activeStations = STATIONS.filter((s) => s.isActive)
  const inactiveStations = STATIONS.filter((s) => !s.isActive)

  return (
    <div className="grid h-screen grid-rows-[auto_auto_auto_minmax(0,1fr)] bg-background text-foreground overflow-hidden">
      <Header isConnected={isConnected} />

      {/* Manual mode banner */}
      {isManual && (
        <div
          className="flex items-center justify-center gap-2 py-1.5 text-xs font-bold tracking-widest uppercase"
          style={{
            background:
              "repeating-linear-gradient(45deg, #facc15 0px, #facc15 12px, #111 12px, #111 24px)",
            color: "#000",
          }}
        >
          <span className="bg-black/85 text-yellow-300 rounded px-3 py-0.5 tracking-widest">
            ⚠ Manual Override — Auto sequence suspended ⚠
          </span>
        </div>
      )}

      {/* Unified status strip — everything that used to be top + bottom rails */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-1.5 border-b border-border/40 bg-card/40">
        <PowerIndicator isPowered={isPowered} />
        <div className="w-px h-4 bg-border/50" />
        <RobotStatusBar data={data} />
        {inactiveStations.length > 0 && (
          <>
            <div className="w-px h-4 bg-border/50 ml-auto hidden md:block" />
            <div className="hidden md:flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
                Coming Soon
              </span>
              {inactiveStations.map((s) => (
                <Badge
                  key={s.id}
                  variant="outline"
                  className="text-[10px] font-mono opacity-70 gap-1"
                >
                  <CircleDashed className="size-2.5" />
                  {s.label.replace(" Station", "")}
                </Badge>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Main work area — no page-level scroll on desktop */}
      <main className="relative min-h-0 p-3 md:p-3.5">
        {/* Power-off overlay */}
        {!isPowered && isConnected && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/85 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 rounded-xl border border-border/50 bg-card px-8 py-6 shadow-xl">
              <span className="size-3 rounded-full bg-red-500 shadow-lg shadow-red-500/40 animate-pulse" />
              <span className="text-sm font-mono font-bold text-muted-foreground uppercase tracking-widest">
                System Power Disconnected
              </span>
              <span className="text-xs text-muted-foreground/60">
                Waiting for MPRC power signal…
              </span>
            </div>
          </div>
        )}

        {/*
          Desktop (lg+): two-column grid, no scroll. Left = stations row + cameras row.
                        Right = 3D viewer filling full main height.
          Smaller screens: stacks, page scrolls.
        */}
        <div
          className={`grid h-full gap-3 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,420px)] grid-rows-[auto] lg:grid-rows-1 transition-opacity duration-500 ${
            !isPowered && isConnected ? "opacity-20 pointer-events-none" : "opacity-100"
          }`}
        >
          {/* Left column — stations expand, cameras keep 16:9 aspect */}
          <div className="grid gap-3 min-h-0 grid-rows-[minmax(0,1fr)_auto]">
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 min-h-0">
              {activeStations.map((station) => (
                <StationCard key={station.id} station={station} data={data} />
              ))}
            </div>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <CameraStream id="1" label="Camera 1 — Conveyor / Assembly" />
              <CameraStream id="2" label="Camera 2 — Packaging / Exit" />
            </div>
          </div>

          {/* Right column — 3D viewer fills available height */}
          <div className="min-h-[320px] lg:min-h-0">
            <StepModelViewer />
          </div>
        </div>
      </main>
    </div>
  )
}
